import { useAuth } from "@/auth/AuthProvider";
import { SignalRequestDialog } from "@/components/submissions/SignalRequestDialog";
import type { BrokerQuote } from "@/lib/submissions/brokerSubmissions";
import {
  requestUnderwriterReview,
  REVIEW_ACTION_LABEL,
  REVIEW_NOTE_MAX,
  reviewChip,
  reviewLine,
  reviewRefusalOf,
} from "@/lib/submissions/quoteReview";

/**
 * "Request Underwriter Review" on a referred quote (BRK-S463, PMS-S871): its
 * own dialog (`SignalRequestDialog`), opened from the quote popup's Actions
 * menu (BRK-S465), which offers it only where the broker can ask
 * (`canAskForReview`). Sending opens a `referral_review` action for
 * Signal's underwriters, with an optional note.
 */
export function RequestUnderwriterReviewDialog({
  submissionId,
  quote,
  carrier,
  open,
  onClose,
  onChanged,
}: {
  submissionId: string;
  quote: BrokerQuote;
  carrier: string;
  open: boolean;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const { authedRequest } = useAuth();
  return (
    <SignalRequestDialog
      open={open}
      onClose={onClose}
      title={REVIEW_ACTION_LABEL}
      intro={`Signal takes ${carrier}’s referred quote to its underwriter, who answers Signal by email or phone. You’ll see the answer here.`}
      noteMax={REVIEW_NOTE_MAX}
      noteHelp="Anything that helps the review, such as the loss runs, goes on the submission."
      send={(idempotencyKey, note) =>
        requestUnderwriterReview(authedRequest, submissionId, quote.id, {
          idempotencyKey,
          note,
        })
      }
      refusalOf={reviewRefusalOf}
      answered={(review) => ({
        chip: reviewChip(review.status),
        line: `${reviewLine(review.status, carrier)} This quote isn’t bindable until it’s approved.`,
      })}
      onChanged={onChanged}
    />
  );
}
